import { faEdit, faTrashAlt } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import SyslogConfigService from '../../service/SyslogConfigService'
import { formatBooleanColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const SyslogIntegration: GatewayType = () => {
  const { t } = useTranslation()
  const [syslogConfig, setSyslogConfig] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const context = useContext(DialogContext)
  const { userPermissions } = useContext(AppContext)

  useEffect(() => {
    SyslogConfigService.getConfig()
      .then((syslogConfig) => setSyslogConfig(syslogConfig))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    const { confirmDialogPropsSetter, editDialogPropsSetter } = context
    return [
      {
        text: t('host.text'),
        dataField: 'host',
        sort: true,
        classes: 'position-relative',
        formatter: (cellData: any, row: any) => {
          return AuthService.hasPermission(userPermissions, Authorization.settings.syslogIntegration.edit) ? (
            <ProtectedButton
              variant='link'
              className='text-decoration-none pl-0 stretched-link text-left'
              tooltip={t('edit')}
              onClick={() => editSyslogDialog(t, editDialogPropsSetter, setSyslogConfig, row)}>
              <FontAwesomeIcon icon={faEdit} /> {cellData}
            </ProtectedButton>
          ) : (
            cellData
          )
        },
      },
      {
        text: t('port.text'),
        dataField: 'port',
        sort: true,
      },
      {
        text: t('protocol.text'),
        dataField: 'protocol',
        sort: true,
      },
      {
        text: t('active'),
        dataField: 'active',
        formatter: (cell: any) => formatBooleanColumn(cell),
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.settings.syslogIntegration.edit),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        formatter: (cellData: any, row: any) => {
          return (
            <ProtectedButton
              permissions={Authorization.settings.syslogIntegration.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteSyslogDialog(t, confirmDialogPropsSetter, setSyslogConfig, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          )
        },
      },
    ]
  }, [t, userPermissions, context])

  const defaultSorted = [
    {
      dataField: 'host',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={syslogConfig}
        columns={columns}
        searchable={false}
        defaultSorted={defaultSorted}
        caption={t('syslogConfig.text')}
        helpLink={t('syslogConfig.helpLink')}
        captionButtons={[
          {
            onClick: () => createSyslogDialog(t, context.editDialogPropsSetter, setSyslogConfig),
            text: t('new'),
            permissions: Authorization.settings.syslogIntegration.edit,
          },
        ]}
      />
    </Loading>
  )
}

function createSyslogDialog(t: TFunction, editDialogPropsSetter: any, setSyslogConfig: React.Dispatch<any[]>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('syslogConfig.createHeader'),
    onSubmit: SyslogConfigService.createConfig,
    onSubmitExtraArgs: [t, setSyslogConfig],
    data: [
      {
        id: 'host',
        type: 'text',
        label: t('host.text'),
        placeholder: t('host.text'),
        requiredMessage: t('host.inValidMessage'),
        required: true,
      },
      {
        id: 'port',
        type: 'number',
        label: t('port.text'),
        placeholder: t('port.text'),
        required: true,
        requiredMessage: t('port.inValidMessage'),
        min: '1',
        minInvalidMessage: t('port.rangeInValidMessage'),
        max: '65535',
        maxInvalidMessage: t('port.rangeInValidMessage'),
      },
      {
        id: 'protocol',
        type: 'select',
        options: ['UDP', 'TCP'],
        value: 'UDP',
        label: t('protocol.text'),
        required: true,
      },
      {
        groupLabel: t('active'),
        groupName: 'active',
        type: 'switch',
        checkboxes: [
          {
            id: 'active',
          },
        ],
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function editSyslogDialog(t: TFunction, editDialogPropsSetter: any, setSyslogConfig: React.Dispatch<any[]>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('syslogConfig.updateHeader'),
    onSubmit: SyslogConfigService.updateConfig,
    onSubmitExtraArgs: [t, setSyslogConfig],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'host',
        value: row.host,
        type: 'text',
        label: t('host.text'),
        placeholder: t('host.text'),
        requiredMessage: t('host.inValidMessage'),
        required: true,
      },
      {
        id: 'port',
        value: row.port,
        type: 'number',
        label: t('port.text'),
        placeholder: t('port.text'),
        required: true,
        requiredMessage: t('port.inValidMessage'),
        min: '1',
        minInvalidMessage: t('port.rangeInValidMessage'),
        max: '65535',
        maxInvalidMessage: t('port.rangeInValidMessage'),
      },
      {
        id: 'protocol',
        type: 'select',
        options: ['UDP', 'TCP'],
        value: row.protocol,
        label: t('protocol.text'),
        required: true,
      },
      {
        groupLabel: t('active'),
        groupName: 'active',
        type: 'switch',
        checkboxes: [
          {
            id: 'active',
            value: row.active,
          },
        ],
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteSyslogDialog(t: TFunction, confirmDialogPropsSetter: any, setSyslogConfig: React.Dispatch<any[]>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('syslogConfig.deleteHeader'),
    body: t('syslogConfig.deleteBody', { name: `${row.host}: ${row.port} - ${row.protocol}` }),
    submitText: t('yes'),
    onSubmit: SyslogConfigService.deleteConfig,
    onSubmitExtraArgs: [row.id, t, setSyslogConfig],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

export default SyslogIntegration
